import { courseScheduleAllowsBooking } from "./course-schedule";
import type { LocalOnlineCourseListItem } from "@/lib/online-courses-shared";
import {
  calculateOnlineBogofDiscount,
  isOnlineBogofCampaignActive,
  isOnlineBogofEligibleCourse,
  ONLINE_BOGOF_DISCOUNT_LABEL,
} from "@/lib/online-course-bogof";
import { calculateOnlineMultiBuyDiscount } from "@/lib/online-course-multibuy";

/** Onsite/online courses that must not appear in the CPD package builder. */
const CPD_PACKAGE_EXCLUDED_URL_PATHS = [
  "/courses/aesthetics/foundation-dermal-fillers",
] as const;

export const isExcludedFromCpdPackage = (course: {
  title?: string | null;
  url?: string | null;
  slug?: string | null;
}) => {
  const normalizedUrl = String(course.url || "")
    .toLowerCase()
    .replace(/\/+$/, "");

  if (
    CPD_PACKAGE_EXCLUDED_URL_PATHS.some((path) =>
      normalizedUrl.endsWith(path) || normalizedUrl.includes(`${path}/`),
    )
  ) {
    return true;
  }

  const slug = String(course.slug || "").toLowerCase();
  if (slug.includes("foundation-dermal-filler")) {
    return true;
  }

  return /foundation\s+dermal\s+filler/i.test(String(course.title || ""));
};

export const CPD_PACKAGE_CATEGORY_OPTIONS = [
  { id: "onsite", label: "Onsite" },
  { id: "online", label: "Online" },
] as const;

export type CpdPackageCategoryFilter =
  (typeof CPD_PACKAGE_CATEGORY_OPTIONS)[number]["id"];

export type PracticalCpdCourse = {
  id: string;
  title: string;
  url: string;
  imageUrl: string | null;
  standardPricePence: number | null;
  wasPricePence: number | null;
  categories: string[];
};

type PracticalEventSeat = {
  price?: number | null;
  seat_price?: number | null;
  seats?: number | null;
  registrations?: Array<{ id: string }> | null;
  countStudentRegistrations?: {
    aggregate?: { count?: number | null } | null;
  } | null;
};

export type PracticalEventDetailPricing = {
  standardPrice?: number | null;
  wasPrice?: number | null;
  package?: boolean | null;
  events?: PracticalEventSeat[] | null;
};

/** Match course listing cards: prefer next available seat price, else event_detail standard. */
export const resolvePracticalCourseListingPrice = (
  event: PracticalEventDetailPricing | null | undefined,
): { standardPricePence: number | null; wasPricePence: number | null } => {
  if (!event) {
    return { standardPricePence: null, wasPricePence: null };
  }

  const standardPrice = Number(event.standardPrice || 0);
  const wasPrice = Number(event.wasPrice || 0);

  const availableEvents = (event.events || []).filter((seat) => {
    if ((seat as any).courseSchedule && !courseScheduleAllowsBooking(seat as any)) return false;
    const seats = Number(seat.seats || 0);
    const booked =
      seat.countStudentRegistrations?.aggregate?.count ??
      seat.registrations?.length ??
      0;

    return seats - Number(booked) > 0;
  });

  const nextSeatPrice = Number(
    availableEvents[0]?.price ?? availableEvents[0]?.seat_price ?? 0,
  );

  if (nextSeatPrice > 0) {
    const comparePrice =
      wasPrice > 0
        ? wasPrice
        : standardPrice > nextSeatPrice
          ? standardPrice
          : 0;

    return {
      standardPricePence: nextSeatPrice,
      wasPricePence:
        comparePrice > 0 && comparePrice > nextSeatPrice ? comparePrice : null,
    };
  }

  if (standardPrice > 0) {
    return {
      standardPricePence: standardPrice,
      wasPricePence:
        wasPrice > 0 && wasPrice > standardPrice ? wasPrice : null,
    };
  }

  return { standardPricePence: null, wasPricePence: null };
};

export const isOnlinePackageCategory = (
  category: CpdPackageCategoryFilter,
): category is "online" => category === "online";

export const isOnsitePackageCategory = (
  category: CpdPackageCategoryFilter,
): category is "onsite" => category === "onsite";

export const filterOnlineCpdCourses = ({
  courses,
  query,
}: {
  courses: LocalOnlineCourseListItem[];
  query: string;
}) => {
  const normalizedQuery = query.trim().toLowerCase();

  return courses.filter((course) => {
    if (!normalizedQuery) {
      return true;
    }

    const haystack = [
      course.title,
      course.one_line_outcome,
      course.audience_summary,
      ...(course.categories || []),
    ]
      .join(" ")
      .toLowerCase();

    return haystack.includes(normalizedQuery);
  });
};

export const filterOnsiteCpdCourses = ({
  courses,
  query,
}: {
  courses: PracticalCpdCourse[];
  query: string;
}) => {
  const normalizedQuery = query.trim().toLowerCase();

  return courses.filter((course) => {
    if (!normalizedQuery) {
      return true;
    }

    const haystack = [course.title, course.url, ...course.categories]
      .join(" ")
      .toLowerCase();

    return haystack.includes(normalizedQuery);
  });
};

/** @deprecated Use filterOnsiteCpdCourses */
export const filterPracticalCpdCourses = filterOnsiteCpdCourses;

export const BUILD_YOUR_CPD_PACKAGE_URL =
  "https://www.cosmetic.college/build-your-cpd-package/";

export const ONSITE_CPD_PACKAGE_COURSE_COUNT = 4;
export const ONSITE_CPD_PACKAGE_PRICE_PENCE = 99900;

export function getEventDetailCategory(attributes: unknown): string | null {
  if (!attributes) {
    return null;
  }

  let record: Record<string, unknown> | null = null;

  if (typeof attributes === "string") {
    try {
      const parsed = JSON.parse(attributes);
      record =
        typeof parsed === "object" && parsed !== null
          ? (parsed as Record<string, unknown>)
          : null;
    } catch {
      return null;
    }
  } else if (typeof attributes === "object") {
    record = attributes as Record<string, unknown>;
  }

  const category = record?.category;
  return typeof category === "string" ? category : null;
}

export function isEventDetailCpdCategory(attributes: unknown): boolean {
  return getEventDetailCategory(attributes)?.toUpperCase() === "CPD";
}

type OnsiteCpdPackageBasketCourse = {
  onsiteCpdPackageCourse?: boolean;
  package?: boolean | null;
  fields?: {
    title?: string | null;
    url?: string | null;
    slug?: string | null;
    type?: string | null;
    categories?: string[] | null;
  } | null;
  event?: {
    package?: boolean | null;
    is_package?: boolean | null;
    type?: string | null;
    title?: string | null;
    attributes?: unknown;
    metadata?: {
      is_online_config?: boolean;
      online_configuration_id?: string | null;
    } | null;
  } | null;
};

const courseCategories = (course: OnsiteCpdPackageBasketCourse) => {
  const categories = course.fields?.categories;
  return Array.isArray(categories)
    ? categories.map((category) => String(category).toLowerCase())
    : [];
};

const courseTitle = (course: OnsiteCpdPackageBasketCourse) =>
  String(course.fields?.title || course.event?.title || "");

const courseUrl = (course: OnsiteCpdPackageBasketCourse) =>
  String(course.fields?.url || "").toLowerCase();

/** VTCT, packages, online courses, and other non-CPD items cannot join the fixed 4-course price. */
export function isDisqualifiedFromOnsiteCpdPackage(course: unknown): boolean {
  if (!course || typeof course !== "object") {
    return true;
  }

  const basketCourse = course as OnsiteCpdPackageBasketCourse;
  const categories = courseCategories(basketCourse);
  const title = courseTitle(basketCourse);
  const url = courseUrl(basketCourse);
  const attributeCategory = getEventDetailCategory(
    basketCourse.event?.attributes,
  )?.toUpperCase();
  const eventType = String(basketCourse.event?.type || "").toUpperCase();

  if (
    basketCourse.package ||
    basketCourse.event?.package ||
    basketCourse.event?.is_package
  ) {
    return true;
  }

  if (
    categories.includes("vtct") ||
    attributeCategory === "VTCT" ||
    /\bvtct\b/i.test(title) ||
    url.includes("/vtct/") ||
    url.includes("/courses/vtct")
  ) {
    return true;
  }

  if (
    isExcludedFromCpdPackage({
      title,
      url,
      slug: basketCourse.fields?.slug,
    })
  ) {
    return true;
  }

  if (
    eventType === "ONLINE" ||
    basketCourse.event?.metadata?.is_online_config ||
    basketCourse.event?.metadata?.online_configuration_id ||
    url.includes("/courses/elearning/")
  ) {
    return true;
  }

  return false;
}

export function isOnsiteCpdPackageCourse(course: unknown): boolean {
  if (isDisqualifiedFromOnsiteCpdPackage(course)) {
    return false;
  }

  const basketCourse = course as OnsiteCpdPackageBasketCourse;
  if (basketCourse.onsiteCpdPackageCourse === true) {
    return true;
  }

  if (isEventDetailCpdCategory(basketCourse.event?.attributes)) {
    return true;
  }

  const categories = courseCategories(basketCourse);
  const type = basketCourse.fields?.type;
  return (
    categories.includes("cpd") &&
    !categories.includes("vtct") &&
    (!type || type === "Course")
  );
}

export function basketQualifiesForOnsiteCpdPackage(courses: unknown): boolean {
  if (
    !Array.isArray(courses) ||
    courses.length !== ONSITE_CPD_PACKAGE_COURSE_COUNT
  ) {
    return false;
  }

  return courses.every((course) => isOnsiteCpdPackageCourse(course));
}

function isContentfulCpdCourse(
  fields: { categories?: string[]; type?: string } | null | undefined,
): boolean {
  if (!fields) {
    return false;
  }
  const categories = Array.isArray(fields.categories) ? fields.categories : [];
  return (
    fields.type === "Course" &&
    categories.includes("cpd") &&
    !categories.includes("vtct")
  );
}

export function checkoutBasketHasCpdCourse(
  courses: Array<{
    event?: { attributes?: unknown } | null;
    fields?: { categories?: string[]; type?: string } | null;
  }>,
): boolean {
  return courses.some((course) => {
    if (course.event && isEventDetailCpdCategory(course.event.attributes)) {
      return true;
    }
    return isContentfulCpdCourse(course.fields);
  });
}
/** @deprecated Checkout uses locked basket pricing instead of this discount code. */
export const ONSITE_CPD_PACKAGE_DISCOUNT_CODE = "CPD999";
export const ONSITE_CPD_PACKAGE_CHECKOUT_FLAG = "onsiteCpdPackage";
export const ONSITE_CPD_PACKAGE_SESSION_KEY = "cc.onsiteCpdPackage";

export const calculateOnsiteCpdPackagePricing = (
  selectedCourses: PracticalCpdCourse[],
) => {
  const count = selectedCourses.length;
  const subtotalPence = selectedCourses.reduce(
    (sum, course) => sum + Number(course.standardPricePence || 0),
    0,
  );
  const qualifies = count === ONSITE_CPD_PACKAGE_COURSE_COUNT;
  const totalPence = qualifies
    ? ONSITE_CPD_PACKAGE_PRICE_PENCE
    : subtotalPence;
  const discountPence = qualifies
    ? Math.max(subtotalPence - ONSITE_CPD_PACKAGE_PRICE_PENCE, 0)
    : 0;

  return {
    count,
    subtotalPence,
    discountPence,
    totalPence,
    qualifies,
    remaining: Math.max(ONSITE_CPD_PACKAGE_COURSE_COUNT - count, 0),
  };
};

export const calculateCpdPackagePricing = (
  selectedCourses: LocalOnlineCourseListItem[],
) => {
  const subtotalPence = selectedCourses.reduce(
    (sum, course) => sum + Number(course.price_pence || 0),
    0,
  );
  const wasTotalPence = selectedCourses.reduce(
    (sum, course) => sum + Number(course.was_price_pence || course.price_pence || 0),
    0,
  );

  if (isOnlineBogofCampaignActive()) {
    const eligibleCoursePricesPence = selectedCourses
      .filter(isOnlineBogofEligibleCourse)
      .map((course) => Number(course.price_pence || 0))
      .filter((price) => price > 0);
    const bogof = calculateOnlineBogofDiscount(eligibleCoursePricesPence);

    return {
      subtotalPence,
      wasTotalPence,
      discountPence: bogof.discountPence,
      totalPence: Math.max(subtotalPence - bogof.discountPence, 0),
      tier:
        bogof.discountPence > 0
          ? { label: ONLINE_BOGOF_DISCOUNT_LABEL }
          : null,
      count: selectedCourses.length,
    };
  }

  const discount = calculateOnlineMultiBuyDiscount(
    selectedCourses.length,
    subtotalPence,
  );

  return {
    subtotalPence,
    wasTotalPence,
    discountPence: discount.discountPence,
    totalPence: discount.finalTotalPence,
    tier: discount.tier,
    count: selectedCourses.length,
  };
};

/** @deprecated Use CPD_PACKAGE_CATEGORY_OPTIONS */
export const CPD_ONLINE_CATEGORY_OPTIONS = CPD_PACKAGE_CATEGORY_OPTIONS;
